import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

/**
 * Validate that uploaded content actually looks like a resume
 */
function validateResumeContent(text, parsedData) {
  // Check minimum text length (resumes should have substantial content)
  if (!text || text.length < 100) {
    return {
      isValid: false,
      error: 'This file appears too short to be a resume. Please upload a complete resume with your work experience, education, and skills.'
    };
  }

  // Check word count (resumes typically have 200+ words)
  const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
  if (wordCount < 50) {
    return {
      isValid: false,
      error: 'This file doesn\'t appear to contain enough information. Please upload a complete resume.'
    };
  }

  // Check for resume-like keywords (at least 2 categories should match)
  const resumeIndicators = {
    experience: /\b(experience|work|employment|position|role|job|responsibilities|duties)\b/i,
    education: /\b(education|degree|bachelor|master|university|college|school|graduated|diploma|certification)\b/i,
    skills: /\b(skills|proficient|experience in|knowledge of|expertise|competencies)\b/i,
    contact: /\b(email|phone|linkedin|address|contact)\b/i,
    professional: /\b(professional|summary|objective|career|achievements|accomplishments)\b/i
  };

  let matchCount = 0;
  for (const [category, pattern] of Object.entries(resumeIndicators)) {
    if (pattern.test(text)) {
      matchCount++;
    }
  }

  if (matchCount < 2) {
    return {
      isValid: false,
      error: 'This doesn\'t appear to be a resume. Please upload a document that includes your work experience, education, and skills.'
    };
  }

  // Check parsed data has at least one key field
  const hasName = parsedData?.name && parsedData.name.length > 0;
  const hasSkills = parsedData?.skills && Array.isArray(parsedData.skills) && parsedData.skills.length > 0;
  const hasPosition = parsedData?.currentPosition && parsedData.currentPosition.length > 0;
  const hasEducation = parsedData?.education && Array.isArray(parsedData.education) && parsedData.education.length > 0;

  if (!hasName && !hasSkills && !hasPosition && !hasEducation) {
    return {
      isValid: false,
      error: 'Unable to find resume information (name, skills, work experience, or education). Please make sure you\'re uploading your resume.'
    };
  }

  return { isValid: true };
}

const ResumeInput = ({ formData, updateFormData, onValidationChange }) => {
  const [tabValue, setTabValue] = useState(0); // 0: Upload, 1: Paste
  const [pastedText, setPastedText] = useState(formData.resumeText || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [parsedInfo, setParsedInfo] = useState(formData.resumeData || null);
  const [uploadedFileName, setUploadedFileName] = useState(formData.fileName || null);

  // Word count
  const wordCount = pastedText ? pastedText.split(/\s+/).filter(w => w.length > 0).length : 0;

  // Validation
  const isValid = wordCount >= 50 && !error;

  // Track previous validation state to avoid infinite loops
  const prevValidRef = useRef(null); // Start with null to ensure first render fires
  
  useEffect(() => {
    // Always fire on first render (prevValidRef is null), then only on changes
    if (prevValidRef.current !== isValid) {
      prevValidRef.current = isValid;
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // Ensure validation fires on mount
  useEffect(() => {
    onValidationChange(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle file upload using cloud function
  const handleFileUpload = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      console.log('📤 Uploading resume for parsing...');

      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Get Firebase project ID from config
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'gptme-1899f';
      const parseUrl = `https://us-central1-${projectId}.cloudfunctions.net/parseResumeFile`;
      
      const response = await fetch(parseUrl, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to parse resume' }));
        throw new Error(errorData.error || 'Failed to parse resume');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to parse resume');
      }

      console.log('✅ Resume parsed successfully');

      // Validate that this looks like a resume
      const validationResult = validateResumeContent(result.text, result.data);
      if (!validationResult.isValid) {
        throw new Error(validationResult.error);
      }

      setPastedText(result.text);
      setParsedInfo(result.data);

      // Update form data and file name
      setUploadedFileName(file.name);
      updateFormData({
        resumeText: result.text,
        resumeData: result.data,
        fileName: file.name
      });

      setUploading(false);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Error processing resume');
      setUploading(false);
    }
  }, [updateFormData]);

  // Handle paste text changes
  const handleTextChange = useCallback((event) => {
    const text = event.target.value;
    setPastedText(text);
    
    if (text.length > 50) {
      const parsedData = parseResumeClient(text);
      
      // Validate the pasted text
      const validationResult = validateResumeContent(text, parsedData);
      if (!validationResult.isValid) {
        setError(validationResult.error);
        setParsedInfo(null);
        return;
      }
      
      setParsedInfo(parsedData);
      
      updateFormData({
        resumeText: text,
        resumeData: parsedData
      });
    }
    
    setError('');
  }, [updateFormData]);

  // Switch tabs
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Get word count color
  const getWordCountColor = () => {
    if (wordCount === 0) return '#737373';
    if (wordCount < 50) return '#ef4444';
    return '#00FFF5';
  };

  // Setup react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxFiles: 1,
    disabled: uploading,
    noClick: false,
    noKeyboard: false
  });

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" sx={{ color: '#fafafa', mb: 1, fontWeight: 600 }}>
        Upload Your Resume
      </Typography>
      <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 3 }}>
        Share your experience, skills, and education to get your personalized career plan.
        Your information is stored securely and used to build your public career profile.
      </Typography>

      {/* Tabs */}
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange}
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            color: '#737373',
            '&.Mui-selected': {
              color: '#00FFF5'
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#00FFF5'
          }
        }}
      >
        <Tab label="Upload File" />
        <Tab label="Paste Text" />
      </Tabs>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Box>
          <Paper
            {...getRootProps()}
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: isDragActive ? '#171717' : '#0a0a0a',
              border: `2px dashed ${isDragActive ? '#00FFF5' : '#262626'}`,
              borderRadius: 2,
              cursor: uploading ? 'default' : 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': !uploading && {
                borderColor: '#00FFF5',
                backgroundColor: '#171717'
              },
              outline: 'none'
            }}
          >
            <input {...getInputProps()} />
            
            {uploading ? (
              <Box>
                <CircularProgress sx={{ color: '#00FFF5', mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#737373' }}>
                  Processing your resume...
                </Typography>
              </Box>
            ) : uploadedFileName && parsedInfo ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CheckIcon sx={{ fontSize: 48, color: '#00FFF5', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#00FFF5', mb: 1 }}>
                  Resume Uploaded Successfully
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  backgroundColor: '#171717', 
                  px: 2, 
                  py: 1, 
                  borderRadius: 1,
                  border: '1px solid #262626',
                  mb: 2
                }}>
                  <DocumentIcon sx={{ color: '#00FFF5', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: '#fafafa', fontFamily: 'monospace' }}>
                    {uploadedFileName}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#737373' }}>
                  Click or drop to upload a different file
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <UploadIcon sx={{ fontSize: 48, color: '#00FFF5', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#fafafa', mb: 1 }}>
                  {isDragActive ? 'Drop your resume here' : 'Click to upload or drag and drop'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#737373' }}>
                  PDF, DOCX, or TXT (Max 5MB)
                </Typography>
              </Box>
            )}
          </Paper>
          
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#737373', textAlign: 'center' }}>
            💡 Your information is stored securely to build your career profile
          </Typography>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <TextField
            multiline
            rows={12}
            fullWidth
            placeholder="Paste your resume here... Include your name, contact info, work experience, education, and skills."
            value={pastedText}
            onChange={handleTextChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0a0a0a',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                '& fieldset': {
                  borderColor: '#262626',
                  borderWidth: 2
                },
                '&:hover fieldset': {
                  borderColor: '#525252'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00FFF5'
                }
              },
              '& .MuiInputBase-input': {
                color: '#fafafa'
              }
            }}
          />
          
          {/* Word Count */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="caption" sx={{ color: '#737373' }}>
              Minimum 50 words recommended
            </Typography>
            <Chip
              label={`${wordCount.toLocaleString()} words`}
              size="small"
              sx={{
                backgroundColor: '#171717',
                color: getWordCountColor(),
                fontWeight: 600,
                border: `1px solid ${getWordCountColor()}`
              }}
            />
          </Box>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mt: 2,
            backgroundColor: '#0a0a0a',
            border: '1px solid #ef4444',
            color: '#fafafa'
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Parsed Info Preview */}
      {parsedInfo && wordCount >= 50 && (
        <Paper
          sx={{
            mt: 3,
            p: 3,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(0,255,245,0.2)',
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
            <CheckIcon sx={{ color: '#00FFF5', fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: '#00FFF5', fontWeight: 600 }}>
              Resume Detected
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
            Your resume looks good! Continue to the next step.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

/**
 * Simple client-side resume parsing
 * (Basic extraction - server will do more sophisticated parsing)
 */
function parseResumeClient(text) {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  // Extract email
  const emailMatch = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  const email = emailMatch ? emailMatch[0] : null;

  // Extract name (heuristic: first non-empty line)
  let name = lines[0] || null;
  if (name && name.length > 50) name = null;

  // Detect common skills
  const commonSkills = [
    'JavaScript', 'Python', 'Java', 'React', 'Node', 'AWS', 'Docker',
    'SQL', 'Git', 'Agile', 'Leadership', 'Management', 'Communication'
  ];
  
  const skills = commonSkills.filter(skill => 
    new RegExp(`\\b${skill}\\b`, 'i').test(text)
  );

  // Try to find current position
  const titlePatterns = [
    /(?:Senior|Junior|Lead)?\s*(?:Software|Web)\s*(?:Developer|Engineer)/i,
    /(?:Project|Product)\s*Manager/i,
    /(?:Data|Business)\s*Analyst/i
  ];

  let currentPosition = null;
  for (const pattern of titlePatterns) {
    const match = text.match(pattern);
    if (match) {
      currentPosition = match[0];
      break;
    }
  }

  return {
    name,
    email,
    currentPosition,
    skills
  };
}

export default ResumeInput;

